import i18nFactory from '@yandex-int/i18n';
import { createSelector } from 'reselect';

import { HintItem } from '../../components/PhrasesSelectorWizard/PhrasesSelectorWizard';
import { getPhrasesSelectorWizardInitialState } from '../../reducers/phrasesSelectorWizard';
import { getKeywordStatPhrases } from '../../service/PhrasesSelectorWizard/PhrasesSelectorWizard';
import { RegionsHelpers } from '../../service/RegionsHelpers/RegionsHelpers';
import { CampaignType } from '../../typings/domain';
import {
    PhrasesSelectorListItem,
    PhrasesSelectorWizardId,
    PhrasesSelectorWizardState
} from '../../typings/phrases';
import { Regions, State } from '../../typings/state';

import * as keyset from './phrasesSelectorWizard.i18n';

const i18n = i18nFactory(keyset);

export function selectPhrasesSelectorWizard(
    state: State,
    wizardId: PhrasesSelectorWizardId
): PhrasesSelectorWizardState {
    return (
        state.phrasesSelectorWizard[wizardId] ||
        getPhrasesSelectorWizardInitialState()
    );
}

export const selectPhrasesSelectorHintItems = createSelector(
    selectPhrasesSelectorWizard,
    (state: State) => state.commonData.regions,
    (
        phrasesSelectorWizard: PhrasesSelectorWizardState,
        regions: Regions | undefined
    ): HintItem[] => {
        const { groupInfo } = phrasesSelectorWizard;

        return groupInfo
            ? [
                  {
                      label: i18n('campaign'),
                      text: groupInfo.campaign.name
                  },
                  {
                      label: i18n('group'),
                      text: groupInfo.name
                  },
                  {
                      label: i18n('region'),
                      text: regions
                          ? RegionsHelpers.getRegionsTexts(
                                regions,
                                groupInfo.regionIds
                            )
                          : ''
                  },
                  {
                      label: i18n('minus-phrases-per-group'),
                      text:
                          groupInfo.minusKeywords.join(', ') ||
                          i18n('not-specified')
                  }
              ]
            : [];
    }
);

export const selectPhrasesSelectorKeywordStatPhrases = createSelector(
    selectPhrasesSelectorWizard,
    (
        phrasesSelectorWizard: PhrasesSelectorWizardState
    ): {
        similarPhrases: PhrasesSelectorListItem[];
        searchedAlsoPhrases: PhrasesSelectorListItem[];
    } => {
        const {
            startPhrases,
            chosenPhrases,
            keywordsStat
        } = phrasesSelectorWizard;

        return getKeywordStatPhrases(startPhrases, chosenPhrases, keywordsStat);
    }
);

export const selectPhrasesSelectorPriceContextEnabled = createSelector(
    (state: State, wizardId: PhrasesSelectorWizardId) =>
        selectPhrasesSelectorWizard(state, wizardId),
    (state: State, wizardId: PhrasesSelectorWizardId) => wizardId,
    (
        phrasesSelectorWizard: PhrasesSelectorWizardState,
        wizardId: PhrasesSelectorWizardId
    ): boolean => {
        const {
            groupInfo: { campaign }
        } = phrasesSelectorWizard;

        return Boolean(
            campaign.type === CampaignType.CPM_BANNER &&
                // Кампаниям с автостратегиями ставка не нужна
                !campaign.strategy.isAutoBudget &&
                // На редактировании групп есть своя "ставка"
                wizardId !== PhrasesSelectorWizardId.editGroupsPage
        );
    }
);

export const selectPhrasesSelectorMinusWordsListEnabled = createSelector(
    (state: State, wizardId: PhrasesSelectorWizardId) =>
        selectPhrasesSelectorWizard(state, wizardId),
    (
        state: State,
        wizardId: PhrasesSelectorWizardId,
        isMinusWordsListEnabled: boolean
    ) => isMinusWordsListEnabled,
    (
        phrasesSelectorWizard: PhrasesSelectorWizardState,
        isMinusWordsListEnabled: boolean
    ): boolean => {
        const {
            groupInfo: { campaign }
        } = phrasesSelectorWizard;

        return Boolean(
            campaign.type !== CampaignType.CPM_BANNER && isMinusWordsListEnabled
        );
    }
);
